import type { ReactNode } from 'react';
import type { DashboardElement } from '@atnt/shared';
import { cn } from '@/shared/lib/cn';
import { dp } from '../units';
import { MarkerView } from './MarkerView';
import { SectionGridView } from './SectionGridView';
import { ClockBody, DateBody, IndicatorBody, MediaBody, TitleBody } from './widgets';

/** Element types whose size is a uniform `scale` of the design size (corner handles in the editor). */
export const SCALE_TYPES = new Set<DashboardElement['type']>(['clock', 'date', 'indicator', 'marker']);

/**
 * The content of an element without positioning (palette previews, popovers).
 * Markers and the section grid position themselves and are rendered by `ElementView` only.
 */
export function ElementBody({ element, showLogo }: { element: DashboardElement; showLogo?: boolean }) {
  switch (element.type) {
    case 'title':
      return <TitleBody element={element} showLogo={showLogo} />;
    case 'clock':
      return <ClockBody element={element} />;
    case 'date':
      return <DateBody element={element} />;
    case 'indicator':
      return <IndicatorBody element={element} />;
    case 'image':
    case 'gif':
    case 'video':
      return <MediaBody element={element} />;
    default:
      return null;
  }
}

export interface ElementViewProps {
  element: DashboardElement;
  /** viewer title block: show the brand logo (the scene passes it to the first title element) */
  showLogo?: boolean;
  className?: string;
  /** extra content positioned with the element (editor handles) */
  children?: ReactNode;
}

/**
 * One dashboard element at its stage position: `(x, y)` = top-left in stage px (markers: dot centre), scale
 * types are scaled about their top-left corner. Carries `data-element-id` / `data-element-type` for hit-testing.
 */
export function ElementView({ element, showLogo, className, children }: ElementViewProps) {
  if (element.type === 'marker') return <MarkerView element={element}>{children}</MarkerView>;
  if (element.type === 'sectionGrid') return <SectionGridView element={element}>{children}</SectionGridView>;
  const scale = 'scale' in element && element.scale !== 1 ? element.scale : null;
  return (
    <div
      data-element-id={element.id}
      data-element-type={element.type}
      className={cn('absolute w-max', className)}
      style={{ left: dp(element.x), top: dp(element.y), transform: scale ? `scale(${scale})` : undefined, transformOrigin: '0 0' }}
    >
      <ElementBody element={element} showLogo={showLogo} />
      {children}
    </div>
  );
}
